'use client';
import { useState, useRef, useEffect } from 'react';
import { useThemePreference } from '@practium/features/theme';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import styles from './PreferencesCard.module.css';

interface PreferencesCardProps {
	notificationsEnabled?: boolean;
	onToggleNotifications?: () => void;
}

export const PreferencesCard = ({
	notificationsEnabled,
	onToggleNotifications,
}: PreferencesCardProps) => {
	const { theme: currentTheme, changeTheme, availableThemes, isThemePreferenceBusy } = useThemePreference();
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const dropdownRef = useRef<HTMLDivElement>(null);

	const selectedTheme = availableThemes.find((t) => t.id === currentTheme) || availableThemes[0];

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
				setIsDropdownOpen(false);
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	return (
		<div className={styles.card}>
			<div className={styles.header}>
				<h3 className={styles.title}>Preferências</h3>
				<p className={styles.subtitle}>Personalize sua experiência</p>
			</div>

			<div className={styles.content}>
				<div className={styles.settingItem}>
					<div className={styles.info}>
						<span className={styles.label}>Tema do Sistema</span>
						<span className={styles.description}>Selecione a paleta de cores da interface</span>
					</div>

					<div className={styles.customSelectWrapper} ref={dropdownRef}>
						<button
							className={`${styles.selectTrigger} ${isDropdownOpen ? styles.active : ''}`}
							onClick={() => setIsDropdownOpen(!isDropdownOpen)}
							disabled={isThemePreferenceBusy}
							type="button"
						>
							<div className={styles.selectedContent}>
								<div
									className={styles.colorCircle}
									style={{ backgroundColor: selectedTheme.color }}
								/>
								<span>{selectedTheme.name}</span>
							</div>
							<KeyboardArrowDownIcon
								className={`${styles.arrowIcon} ${isDropdownOpen ? styles.rotate : ''}`}
							/>
						</button>

						{isDropdownOpen && (
							<div className={styles.dropdownOptions}>
								{availableThemes.map((theme) => (
									<div
										key={theme.id}
										className={`${styles.option} ${currentTheme === theme.id ? styles.selectedOption : ''}`}
										onClick={() => {
											changeTheme(theme.id);
											setIsDropdownOpen(false);
										}}
									>
										<div className={styles.colorCircle} style={{ backgroundColor: theme.color }} />
										<span>{theme.name}</span>
									</div>
								))}
							</div>
						)}
					</div>
				</div>

				<div className={styles.settingItem}>
					<div className={styles.info}>
						<span className={styles.label}>Notificações Push</span>
						<span className={styles.description}>Receba alertas no navegador</span>
					</div>
					<label className={styles.switch}>
						<input
							type="checkbox"
							checked={notificationsEnabled}
							onChange={onToggleNotifications}
						/>
						<span className={styles.slider} />
					</label>
				</div>
			</div>
		</div>
	);
};
